/**
 * useAiTopic — one hook to power every "empty content → auto-generate"
 * flow across the site.
 *
 *   const { doc, status, retry } = useAiTopic({
 *     source: "weather",           // weather | location | poi | nature | topic | generic
 *     entityKey: "pests",          // stable ID for this topic
 *     title: "Pest Forecast",      // human title used by the LLM
 *     context: "hints for LLM…",   // optional
 *     deepLink: "/weather/topic/pests",
 *     enabled: true,               // pass `false` to disable
 *   });
 *
 * status ∈ "idle" | "pending" | "generating" | "ready" | "failed" | "missing"
 *
 * The hook polls every 5 s while status ∈ {pending, generating}. Once
 * ready (or failed) polling stops.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";

const POLL_MS = 5000;

// URL-safe slug used to fetch a topic doc directly. Mirrors
// `_slugify` in backend/routes/ai_topics.py.
export function slugifyTopic(source, entityKey) {
  const raw = [source, entityKey].filter(Boolean).join("-").toLowerCase();
  return raw.replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 180) || "topic";
}

export default function useAiTopic({ source, entityKey, title, context, deepLink, enabled = true }) {
  const [doc, setDoc]       = useState(null);
  const [status, setStatus] = useState("idle");
  const timer = useRef(null);

  const slug = enabled && source && entityKey ? slugifyTopic(source, entityKey) : null;

  const stop = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
  }, []);

  const kick = useCallback(async () => {
    if (!slug) return;
    try {
      const r = await http.post(`/ai-topics/request`, {
        source, entity_key: entityKey, title, context: context || null,
        deep_link: deepLink || null,
      });
      setDoc(r.data);
      setStatus(r.data.status || "pending");
    } catch (e) {
      setStatus("failed");
    }
  }, [slug, source, entityKey, title, context, deepLink]);

  const load = useCallback(async () => {
    if (!slug) return;
    try {
      const r = await http.get(`/ai-topics/${slug}`);
      setDoc(r.data);
      setStatus(r.data.status || "missing");
      return r.data.status;
    } catch (e) {
      if (e?.response?.status === 404) {
        setStatus("missing");
        return "missing";
      }
      setStatus("failed");
      return "failed";
    }
  }, [slug]);

  // Initial fetch + kick-off if missing.
  useEffect(() => {
    if (!enabled || !slug) return;
    let alive = true;
    (async () => {
      const s = await load();
      if (!alive) return;
      if (s === "missing") {
        await kick();
      }
    })();
    return () => { alive = false; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, enabled]);

  // Poll while pending/generating.
  useEffect(() => {
    if (!enabled || !slug) return;
    stop();
    if (status === "pending" || status === "generating") {
      timer.current = setTimeout(load, POLL_MS);
    }
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, slug, enabled]);

  return { doc, status, retry: kick, slug };
}
